/**
 * `chat-level` — pure helpers for the concierge FAST/NORMAL/DEEP level system
 * (P4 of `ai-docs/lexide-p4-level-chip-miniplan.md`; the completion track of
 * `ai-docs/lexide-promptbox-preflight-and-level-miniplan.md`, gates ratified
 * §3.6.0 2026-08-29).
 *
 * Everything here is node-env-testable: the reducer that turns a
 * `lx.get_chat_level` payload into UI state, the definitive-vs-retryable
 * write-fail discriminator, and the L-9 gate's reason→string-key routing.
 *
 * ## The three honesty rules this module encodes (do not weaken)
 *
 * 1. **A failed call is `unknown`, never a level.** Contract AC-1: no code
 *    path may render a transport failure as NORMAL/org-default. The reducer
 *    maps anything malformed to `{ kind: "unknown" }`.
 * 2. **`org-default` is NOT always a level.** Until gate L-2's repoint lands,
 *    the org default is the `LOW` tier — none of the three levels — so
 *    `level` is nullable there and the chip must not present it as a pick
 *    (contract AC-2; the 1024-cap incident class).
 * 3. **Definitiveness survives the write path.** A definitive break comes
 *    back as a structured RESULT (`{ok:false, definitive:true, reason,
 *    detail}`), never a thrown error — so retry middleware cannot eat it
 *    (P3 as landed, `gateway-methods/chat-level.ts`). Route it to the L-9
 *    gate; route thrown errors to a retry toast. Collapsing the two
 *    re-creates the config-break-wearing-a-retry-costume defect.
 */

/** Wire codes stay English (gate L-1); labels localize via i18n. */
export type ConciergeMode = "FAST" | "NORMAL" | "DEEP";

/** Display order — cheap→deep, mirroring the pick popover top-to-bottom. */
export const CHAT_LEVELS: readonly ConciergeMode[] = ["FAST", "NORMAL", "DEEP"];

export function isConciergeMode(v: unknown): v is ConciergeMode {
  return v === "FAST" || v === "NORMAL" || v === "DEEP";
}

/** i18n key for a level's label (Rápido/Normal/Profundo per L-1). */
export function levelLabelKey(level: ConciergeMode): string {
  return `chat.level.label.${level.toLowerCase()}`;
}

/** i18n key for a level's one-line explanation (operator copy, 2026-08-29). */
export function levelExplanationKey(level: ConciergeMode): string {
  return `chat.level.explanation.${level.toLowerCase()}`;
}

/* ------------------------------------------------------------------ */
/* Read-back reducer (contract v1.3 §1 → UI state)                     */
/* ------------------------------------------------------------------ */

export interface ResolvedRef {
  provider: string;
  model: string;
  thinking?: string;
}

export interface SelfHealNotice {
  previousLevel: ConciergeMode;
  reason: string;
  at: string;
}

/** UI-facing state. Mirrors the contract's kinds plus the client-only
 *  `unknown` (the call itself failed — requirement 1's third outcome). */
export type ChatLevelUiState =
  | {
      kind: "session-level";
      level: ConciergeMode;
      alias: string;
      resolved: ResolvedRef | null;
    }
  | {
      /** No session pick; the org default applies. `level: null` means the
       *  default corresponds to NO level (today's normal case) — render the
       *  resolved ref, never a level name. */
      kind: "org-default";
      level: ConciergeMode | null;
      profileName: string | null;
      resolved: ResolvedRef | null;
      selfHealed: SelfHealNotice | null;
    }
  | { kind: "unavailable"; reason: string; detail: string }
  /** The call itself failed. `retrying` distinguishes "we have not got an
   *  answer YET and are still trying" from a settled unknown — the chip must
   *  never render a transient failure with the finality of a definitive one
   *  (i-ter, 2026-09-12). Absent/false = settled. */
  | { kind: "unknown"; retrying?: boolean };

function asRef(v: unknown): ResolvedRef | null {
  if (typeof v !== "object" || v === null) return null;
  const o = v as Record<string, unknown>;
  if (typeof o.provider !== "string" || typeof o.model !== "string") return null;
  return {
    provider: o.provider,
    model: o.model,
    ...(typeof o.thinking === "string" ? { thinking: o.thinking } : {}),
  };
}

function asSelfHeal(v: unknown): SelfHealNotice | null {
  if (typeof v !== "object" || v === null) return null;
  const o = v as Record<string, unknown>;
  if (!isConciergeMode(o.previousLevel)) return null;
  if (typeof o.reason !== "string" || typeof o.at !== "string") return null;
  return { previousLevel: o.previousLevel, reason: o.reason, at: o.at };
}

/**
 * Reduce a `lx.get_chat_level` payload to UI state. TOTAL: any shape it does
 * not positively recognize — including `null`, a thrown-and-caught error the
 * caller passed in, or a future kind — reduces to `{ kind: "unknown" }`.
 * It never guesses a level (the collapse-onto-NORMAL defect is one line to
 * write and invisible to review — this function is where it is forbidden).
 */
export function reduceChatLevelReadback(payload: unknown): ChatLevelUiState {
  if (typeof payload !== "object" || payload === null) return { kind: "unknown" };
  const p = payload as Record<string, unknown>;

  switch (p.kind) {
    case "session-level": {
      if (!isConciergeMode(p.level) || typeof p.alias !== "string") {
        return { kind: "unknown" };
      }
      return {
        kind: "session-level",
        level: p.level,
        alias: p.alias,
        resolved: asRef(p.resolved),
      };
    }
    case "org-default": {
      // `level` may be a mode, null, or absent — absent normalizes to null
      // (no level), NEVER to a mode.
      const level = isConciergeMode(p.level) ? p.level : null;
      return {
        kind: "org-default",
        level,
        profileName: typeof p.profileName === "string" ? p.profileName : null,
        resolved: asRef(p.resolved),
        selfHealed: asSelfHeal(p.selfHealed),
      };
    }
    case "unavailable": {
      if (typeof p.reason !== "string") return { kind: "unknown" };
      return {
        kind: "unavailable",
        reason: p.reason,
        detail: typeof p.detail === "string" ? p.detail : "",
      };
    }
    default:
      return { kind: "unknown" };
  }
}

/* ------------------------------------------------------------------ */
/* Write-path discriminator (P3 transport encoding)                    */
/* ------------------------------------------------------------------ */

export interface DefinitiveFail {
  ok: false;
  definitive: true;
  reason: string;
  detail: string;
}

/** A definitive config break returned as a structured RESULT by
 *  `lx.set_chat_level` — route to the L-9 gate, never a retry toast. */
export function isDefinitiveFail(v: unknown): v is DefinitiveFail {
  if (typeof v !== "object" || v === null) return false;
  const o = v as Record<string, unknown>;
  return o.ok === false && o.definitive === true && typeof o.reason === "string";
}

/** ⚖ Contract v1.7 §D, ruled (ii) by this lane 2026-08-31: a WRITE-path
 *  rejection — the seam verified the upstream patch and it did NOT apply, so
 *  it refused to write the store. A THIRD class, deliberately outside both
 *  existing ones:
 *   - NOT `definitive` (the field is absent by construction, so
 *     `isDefinitiveFail` is false and L-9 can never fire on it): L-9 means
 *     "the catalog is broken", and one pick failing to install does not;
 *   - NOT the thrown/retryable class either: the call SUCCEEDED and answered
 *     honestly, so a bare "try again" would misdescribe it.
 *  The pick simply did not take effect, and the UI says exactly that while
 *  the chip keeps rendering the previous — still true — state. */
export type WritePathRejectionReason = "pin_not_applied" | "clear_not_applied";

/** The write-path rejection reasons this consumer recognizes. `clear_not_applied`
 *  is the CLEAR path's mirror (a null-patch that reported success while the old
 *  pin still serves) — reserved here so the sibling amendment needs no SPA
 *  change to be handled honestly; no producer emits it yet. */
export const WRITE_PATH_REJECTIONS: readonly WritePathRejectionReason[] = [
  "pin_not_applied",
  "clear_not_applied",
];

export interface PinNotApplied {
  ok: false;
  applied: false;
  reason: WritePathRejectionReason;
  detail: string;
}

export function isPinNotApplied(v: unknown): v is PinNotApplied {
  if (typeof v !== "object" || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    o.ok === false &&
    o.applied === false &&
    typeof o.reason === "string" &&
    (WRITE_PATH_REJECTIONS as readonly string[]).includes(o.reason) &&
    o.definitive !== true // a shape claiming BOTH is malformed — never route it here
  );
}

/* ------------------------------------------------------------------ */
/* L-9 gate copy routing — SIX strings (ratified amendment, §3.6.0)    */
/* ------------------------------------------------------------------ */

/**
 * The six L-9 gate string keys. Four definitive reasons, two of which fork
 * on `detail` (`lexide-p4-level-chip-miniplan.md` §4-bis — each string names
 * the ACTUAL fault and the door that owns it; a shared door never excuses a
 * shared string when the verbs differ):
 *
 *  - `missing_alias`                         → missingAlias   (add the row)
 *  - `no_catalog` + detail "catalog_absent"  → noCatalogAbsent (seed it)
 *  - `no_catalog` + other/construction       → noCatalogBroken (inspect it)
 *  - `no_usable_provider`                    → noUsableProvider (org creds)
 *  - `alias_unresolvable` + NO_AVAILABLE     → aliasNoCredential (org creds)
 *  - `alias_unresolvable` + other/bad row    → aliasBadRow (platform row)
 */
export type GateStringKey =
  | "missingAlias"
  | "noCatalogAbsent"
  | "noCatalogBroken"
  | "noUsableProvider"
  | "aliasNoCredential"
  | "aliasBadRow"
  | "unknownReason";

export const GATE_STRING_KEYS: readonly GateStringKey[] = [
  "missingAlias",
  "noCatalogAbsent",
  "noCatalogBroken",
  "noUsableProvider",
  "aliasNoCredential",
  "aliasBadRow",
  "unknownReason",
];

/**
 * Route a definitive `{reason, detail}` to its gate string key. TOTAL: an
 * unrecognized reason routes to `unknownReason` (the gate still blocks —
 * fail-CLOSED — it just cannot name a door; it shows the raw reason+detail
 * instead of guessing one).
 */
export function gateStringKey(reason: string, detail: string): GateStringKey {
  switch (reason) {
    case "missing_alias":
      return "missingAlias";
    case "no_catalog":
      return detail.includes("catalog_absent") ? "noCatalogAbsent" : "noCatalogBroken";
    case "no_usable_provider":
      return "noUsableProvider";
    case "alias_unresolvable":
      return detail.includes("LLM_NO_AVAILABLE_MODEL")
        ? "aliasNoCredential"
        : "aliasBadRow";
    default:
      return "unknownReason";
  }
}

/** Full i18n key for a gate string. */
export function gateStringI18nKey(key: GateStringKey): string {
  return `chat.level.gate.${key}`;
}

/**
 * Parse a GENERIC concierge profile name to its level — the same rule the
 * v1.6 seam applies server-side (`levelFromProfileName`, generic-only, per
 * ⚖ R-A/R-C in `level-aliases-simplification-miniplan.md`). Displaying given
 * data, not re-deriving policy: the name IS the org's stored chatProfile.
 *
 *  - bare `CONCIERGE_FAST|NORMAL|DEEP` → that level (the R-D stamp shape);
 *  - a `:thinking` composite still NAMES the level row it resolves through,
 *    so it matches too (legacy orgs; the rider dropped localdev's);
 *  - the RETIRED provider-suffixed names (`CONCIERGE_OPENAI_FAST`) do NOT
 *    match — under R-A they no longer exist as catalog rows, and marking a
 *    level for a name that cannot resolve would be a claim, not a display.
 */
export function levelFromGenericProfileName(
  name: string | null | undefined,
): ConciergeMode | null {
  if (!name) return null;
  const m = /^CONCIERGE_(FAST|NORMAL|DEEP)(?::(?:minimal|low|medium|high|xhigh|max))?$/.exec(
    name,
  );
  return m ? (m[1] as ConciergeMode) : null;
}

/** The `:thinking` suffix a profile NAME declares (`"LOW:low"` → `"low"`),
 *  else null. The vocabulary is the resolver's SIX levels — minimal, low,
 *  medium, high, xhigh, max (`lx-llm-resolver/src/types.ts`); `max` is a REAL
 *  sixth level, not an alias for `xhigh`, and as a composite suffix it means
 *  "follow the ceiling" rather than a fixed rung (⚖ §S6-sexies). Displaying given data — the org's stored composite — never a
 *  derivation. Shared by the chip tooltip and the dialog's off-level line. */
export function profileNameThinkingSuffix(name: string | null | undefined): string | null {
  return name?.match(/:(minimal|low|medium|high|xhigh|max)$/)?.[1] ?? null;
}

/**
 * The chip tooltip's REF half — `provider/model · thinking` — composed from
 * server-given pieces only (operator instruction 2026-08-30: the tooltip must
 * reflect the selection, e.g. "Rápido: openai-codex/gpt-5.6-luna · minimal";
 * the label prefix is the callsite's, this builds the ref).
 *
 * Fallback order per piece, given data only — never invented:
 *  - base ref: the read-back's own `resolved`, else the marked level's
 *    `lx.get_chat_levels` row (both server-resolved; the pre-roll org-default
 *    read-back carries no `thinking`, the row does);
 *  - thinking: read-back `thinking` → the profile NAME's declared `:suffix`
 *    → the row's `thinking`. Absent everywhere → omitted, not guessed.
 */
export function chipFullRef(
  stateRef: ResolvedRef | null,
  nameSuffix: string | null,
  rowRef: ResolvedRef | null,
): string | null {
  const base = stateRef ?? rowRef;
  if (!base) return null;
  const thinking = stateRef?.thinking ?? nameSuffix ?? rowRef?.thinking ?? null;
  return `${base.provider}/${base.model}${thinking ? ` · ${thinking}` : ""}`;
}

/**
 * Where the ORG DEFAULT lands among the three levels — for MARKING, not for
 * claiming (operator design ruling 2026-08-29: "the current setting should be
 * marked on the popup … and shown on the badge"; ⚖ R-C 2026-08-30: an
 * untouched session marks FAST because the org default IS `CONCIERGE_FAST`).
 *
 * Resolution order, strongest truth first:
 *  1. the read-back's own `level` (the v1.6 seam computes it server-side);
 *  2. the generic `CONCIERGE_<MODE>` profile NAME (pre-roll bridge — the
 *     deployed v1.4 seam sends `level:null` for the bare generic name; the
 *     name-parse mirrors the seam's own rule so the mark is identical before
 *     and after the roll);
 *  3. unique server-resolved MODEL match (legacy inference for non-concierge
 *     defaults only — under R-B all three rows share one model, so this can
 *     no longer discriminate a concierge-named default and exists for custom
 *     org defaults that happen to land on exactly one row).
 *
 * Deliberate honesty edges:
 *  - a real session pick is not this function's business (returns null — the
 *    checkmark path owns that);
 *  - AMBIGUOUS model match (two levels share the model — the R-B normal
 *    case) → null from step 3, mark nothing;
 *  - a non-concierge default (e.g. `LOW:low`) with an ambiguous model never
 *    reads as a level (AC-2; the 1024-cap incident class).
 */
export function defaultMatchLevel(
  state: ChatLevelUiState | null,
  rows: readonly { level: ConciergeMode; resolved: ResolvedRef | null }[] | null,
): ConciergeMode | null {
  if (!state || state.kind !== "org-default") return null;
  if (state.level) return state.level; // v1.6 seam: server-computed, authoritative
  const byName = levelFromGenericProfileName(state.profileName);
  if (byName) return byName; // pre-roll bridge: the org default NAMES the level
  if (!rows) return null;
  const model = state.resolved?.model;
  if (!model) return null;
  const matches = rows.filter((r) => r.resolved?.model === model);
  return matches.length === 1 ? matches[0].level : null;
}

/**
 * What the L-9 gate blocks on — and ONLY this (fail-CLOSED on a DEFINITIVE
 * break, fail-OPEN on everything else, incl. `unknown`: the inverse of
 * `ChatLLMKeysGate`'s posture is deliberate — miniplan §3(a); blocking on a
 * transient would kill chat on a blip, the regression that component exists
 * to avoid).
 */
export function chatLevelGateInput(
  state: ChatLevelUiState | null,
  definitiveFail: DefinitiveFail | null,
): { reason: string; detail: string } | null {
  if (definitiveFail) {
    return { reason: definitiveFail.reason, detail: definitiveFail.detail };
  }
  if (state?.kind === "unavailable") {
    return { reason: state.reason, detail: state.detail };
  }
  return null;
}
